import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import surveyData from './behavior_survey_v1.json';
import { useTranslation } from 'react-i18next';

const Question = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const isEnglish = (i18n.resolvedLanguage || i18n.language).startsWith('en');
    const currentQIdx = parseInt(id);
    const questions = surveyData.questions;
    const totalQuestions = questions.length;

    // Load answers from localStorage to persist progress
    const [answers, setAnswers] = useState(() => {
        const saved = localStorage.getItem('characterTestAnswers');
        return saved ? JSON.parse(saved) : {};
    });

    const question = questions.find(q => q.question_id === `Q${currentQIdx}`);

    useEffect(() => {
        localStorage.setItem('characterTestAnswers', JSON.stringify(answers));
    }, [answers]);

    useEffect(() => {
        if (!question && currentQIdx <= totalQuestions) {
            // Redirect if ID is invalid but within range (should verify 'Q' prefix logic match)
            // In this case, our logic relies on index matching Q{id}
            // checking if out of bounds
            if (currentQIdx < 1 || currentQIdx > totalQuestions) {
                navigate('/character-test');
            }
        }
    }, [question, currentQIdx, navigate, totalQuestions]);

    if (!question) return null;

    const handleAnswer = (option) => {
        // Save the entire option object including effects
        // We will calculate the score sum in the Result page
        const newAnswers = {
            ...answers,
            [currentQIdx]: {
                optionId: option.option_id,
                effects: option.effects
            }
        };
        setAnswers(newAnswers);

        if (currentQIdx < totalQuestions) {
            navigate(`/character-test/q/${currentQIdx + 1}`);
        } else {
            navigate('/character-test/result');
        }
    };

    const progress = (currentQIdx / totalQuestions) * 100;

    return (
        <div className="test-container">
            <div className="question-card">
                <div className="header">
                    <div className="section-badge">{isEnglish ? (question.section_en || question.section) : question.section}</div>
                    <div className="progress-container">
                        <div className="progress-bar" style={{ width: `${progress}%` }}></div>
                        <span className="progress-text">{currentQIdx} / {totalQuestions}</span>
                    </div>
                </div>

                <h2 className="question-text">{isEnglish ? question.text_en : question.text}</h2>

                <div className="options-container">
                    {question.options.map((option) => (
                        <button
                            key={option.option_id}
                            className="option-btn"
                            onClick={() => handleAnswer(option)}
                        >
                            <span className="option-text">{isEnglish ? option.text_en : option.text}</span>
                        </button>
                    ))}
                </div>

                {currentQIdx > 1 && (
                    <button className="back-btn" onClick={() => navigate(`/character-test/q/${currentQIdx - 1}`)}>
                        {t('common.back', 'Back')}
                    </button>
                )}
            </div>

            <style jsx>{`
                .test-container {
                    min-height: 100vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 1rem;
                    background: #0f172a;
                    color: white;
                }

                .question-card {
                    background: rgba(255, 255, 255, 0.05);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 24px;
                    padding: 2.5rem 2rem;
                    width: 100%;
                    max-width: 600px;
                    min-height: 60vh;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.3);
                }

                .header {
                    margin-bottom: 2rem;
                }

                .section-badge {
                    display: inline-block;
                    font-size: 0.85rem;
                    color: #60a5fa;
                    margin-bottom: 1rem;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }

                .progress-container {
                    position: relative;
                    height: 6px;
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 10px;
                }

                .progress-bar {
                    height: 100%;
                    background: linear-gradient(to right, #3b82f6, #8b5cf6);
                    border-radius: 10px;
                    transition: width 0.3s ease;
                }

                .progress-text {
                    position: absolute;
                    right: 0;
                    top: -25px;
                    font-size: 0.85rem;
                    color: #64748b;
                    font-weight: 500;
                }

                .question-text {
                    font-size: 1.4rem;
                    font-weight: 700;
                    line-height: 1.5;
                    margin-bottom: 3rem;
                    word-break: keep-all;
                    flex: 1;
                }

                .options-container {
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                }

                .option-btn {
                    width: 100%;
                    padding: 1.25rem 1.5rem;
                    background: rgba(255, 255, 255, 0.08);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 16px;
                    color: #cbd5e1;
                    font-size: 1.05rem;
                    text-align: left;
                    cursor: pointer;
                    transition: all 0.2s;
                    line-height: 1.4;
                }

                .option-btn:hover {
                    background: rgba(59, 130, 246, 0.15);
                    border-color: #3b82f6;
                    color: white;
                    transform: translateY(-2px);
                }

                .back-btn {
                    margin-top: 2rem;
                    background: transparent;
                    border: none;
                    color: #64748b;
                    cursor: pointer;
                    font-size: 0.9rem;
                    align-self: flex-start;
                    padding: 0.5rem 0;
                }
                
                .back-btn:hover {
                    color: #94a3b8;
                    text-decoration: underline;
                }

                @media (max-width: 640px) {
                    .question-card {
                        padding: 2rem 1.5rem;
                    }
                    .question-text {
                        font-size: 1.25rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default Question;
